/*
 * Book an Appointment (the appointment page): a private viewing of the pieces the client chooses,
 * booked as on a piece's own page (assets/pdp-viewing.js), in place of LDJ's Calendly page. LDJ
 * (16 Oct 2026): viewings only, and the client chooses the bags; no quotes, drop-offs or anything
 * else. Up to 5 pieces in one viewing (17 Oct), all at one office.
 * 1. The pieces: in-stock pieces from the LDJ Search app, by name or item number (after the first,
 *    only at its office); then in person at that office (Calgary is Megan's, Irvine is Ava's) or
 *    over FaceTime with the same specialist (a piece with no office on record: FaceTime only, by
 *    the shopper's country);
 * 2. a time from that specialist's Google Calendar, in the shopper's own time zone;
 * 3. first name, phone (the FaceTime number for a FaceTime) and email; then the confirmation,
 *    with Add to Your Calendar.
 * POST /api/viewings books it with the first piece's handle, variant, item number and location,
 * exactly like a product page's viewing (reason "visit" in person, "photos" over FaceTime), and the
 * others in `pieces`; the alert, the invite and the Leads page list every piece. Without a connected calendar the same steps send a
 * request the specialist confirms, and if the times can't load the shopper can still leave their
 * details. Markup: snippets/appointment-booking.liquid; styles: service.css (.svc-book).
 */
(() => {
  const root = document.querySelector('[data-svc-book]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';

  const d = root.dataset;
  const q = (selector) => root.querySelector(selector);
  const form = q('[data-svc-book-form]');
  const steps = {};
  root.querySelectorAll('[data-svc-book-step]').forEach((step) => {
    steps[step.dataset.svcBookStep] = step;
  });
  const bar = q('[data-svc-book-bar]');
  const back = q('[data-svc-book-back]');
  const count = q('[data-svc-book-count]');
  const pickError = q('[data-svc-book-pick-error]');
  const toTimes = q('[data-svc-book-to-times]');
  const loadingEl = q('[data-svc-book-loading]');
  const calendar = q('[data-svc-book-calendar]');
  const monthEl = q('[data-svc-book-month]');
  const daysEl = q('[data-svc-book-days]');
  const timesEl = q('[data-svc-book-times]');
  const zoneEl = q('[data-svc-book-zone]');
  const takenEl = q('[data-svc-book-taken]');
  const unavailable = q('[data-svc-book-unavailable]');
  const toDetails = q('[data-svc-book-to-details]');
  const whenEl = q('[data-svc-book-when]');
  const withEl = q('[data-svc-book-with]');
  const errorEl = q('[data-svc-book-error]');
  const submit = q('[data-svc-book-submit]');
  const calendars = q('[data-svc-book-calendars]');
  const phoneLabel = q('[data-svc-book-phone-label]');
  const again = q('[data-svc-book-again]');

  const ORDER = ['1', '2', '3'];
  const STORE = 'ldj:appointment';
  // The pieces' office: Calgary is Megan's, Irvine is Ava's, as on the product pages.
  const OFFICES = {
    Irvine: { specialist: 'us', place: 'our Irvine showroom', label: 'Irvine Showroom', address: '18575 Jamboree Rd, Suite 360' },
    Calgary: { specialist: 'ca', place: 'our Calgary office', label: 'Calgary Office', address: '605 5 Avenue SW, Unit 1010' },
  };

  let current = '1';
  let pieces = [];
  let chosen = null;
  let dayKey = null;
  let shownPlan = '';
  // Open times per specialist and kind (a visit is longer than a FaceTime).
  const cache = new Map();

  // What's ticked, read from the form itself: a reload can bring back a ticked choice
  // (Firefox, Safari) without a change event.
  const picked = (name) => form.elements[name]?.value || '';

  /** Who, how and where: in person only where the pieces are; FaceTime with that same specialist. */
  const plan = () => {
    if (!pieces.length) return null;
    const office = OFFICES[pieces[0].city];
    const where = picked('where');
    if (where === 'visit' && office) return { kind: 'visit', specialist: office.specialist, city: pieces[0].city, place: office.place };
    if (where === 'facetime') {
      const specialist = office ? office.specialist : (d.country || '').toUpperCase() === 'US' ? 'us' : 'ca';
      return { kind: 'facetime', specialist, city: specialist === 'us' ? 'Irvine' : 'Calgary', place: 'FaceTime' };
    }
    return null;
  };
  const keyOfPlan = (p) => (p ? `${p.specialist}:${p.kind}` : '');
  const nameOf = (specialist) => (specialist === 'us' ? d.usName : d.caName) || (specialist === 'us' ? 'Ava' : 'Megan');
  const phoneOf = (specialist) => (specialist === 'us' ? d.usPhone : d.caPhone) || d.mainPhone;
  const isVisit = () => plan()?.kind === 'visit';

  // ------------------------------------------------------------ dates, in the shopper's own zone

  let zone = '';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_error) {
    zone = '';
  }
  const zoneName = (() => {
    for (const style of ['longGeneric', 'long']) {
      try {
        const part = new Intl.DateTimeFormat('en-US', { timeZoneName: style }).formatToParts(new Date()).find((item) => item.type === 'timeZoneName');
        if (part) return part.value;
      } catch (_error) {
        // older browsers: try the next style
      }
    }
    return 'your time zone';
  })();
  const format = (options) => new Intl.DateTimeFormat('en-US', options);
  const keyOf = (date) => format({ year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  // "11:15 AM" and "+1 (949) 555-0123" stay on one line.
  const timeText = (date) => format({ hour: 'numeric', minute: '2-digit' }).format(date).replace(/\s+(?=[AP]M)/, '\u00a0');
  const unbroken = (text) => String(text).replace(/ /g, '\u00a0').replace(/-/g, '\u2011');
  const dayText = (date) => format({ weekday: 'long', month: 'long', day: 'numeric' }).format(date);
  const whenText = (date) => `${dayText(date)} at ${timeText(date)}`;

  // ------------------------------------------------------------ steps

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const show = (name, { focus = true, scroll = true } = {}) => {
    current = name;
    Object.keys(steps).forEach((key) => {
      steps[key].hidden = key !== name;
    });
    const index = ORDER.indexOf(name);
    // Back and the step count from the second step on; none on the first or the confirmation.
    bar.hidden = index < 1;
    if (index >= 0) count.textContent = `Step ${index + 1} of ${ORDER.length}`;
    if (name === '2') renderSlots();
    // On a phone the next step starts below the fold: bring the card's top into view.
    if (scroll) {
      const top = root.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.5) root.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    }
    if (focus) steps[name].querySelector('.svc-book__title')?.focus({ preventScroll: true });
  };

  back.addEventListener('click', () => {
    const index = ORDER.indexOf(current);
    if (index > 0) show(ORDER[index - 1]);
  });

  // ------------------------------------------------------------ 1. the pieces, and how to see them

  // In-stock pieces from the LDJ Search app (the service behind the site's search), by name or
  // item number. Read only, and not counted as a shopper's search. Up to MAX_PIECES pieces, all at
  // one office: the first piece chosen sets it, and from then on the search looks only there.
  const MAX_PIECES = 5;
  const searchEndpoint = d.endpoint.replace(/\/api\/viewings\/?$/, '/api/search');
  const searchInput = q('[data-svc-book-search]');
  const searchStatus = q('[data-svc-book-search-status]');
  const results = q('[data-svc-book-results]');
  const findBox = q('[data-svc-book-find]');
  const findCancel = q('[data-svc-book-find-cancel]');
  const pickedBox = q('[data-svc-book-picked]');
  const chosenList = q('[data-svc-book-chosen]');
  const addButton = q('[data-svc-book-add]');
  const fullNote = q('[data-svc-book-full]');
  const announce = q('[data-svc-book-announce]');
  const howBox = q('[data-svc-book-how]');
  const howLegend = q('[data-svc-book-how-legend]');
  const visitChoice = q('[data-svc-book-visit-choice]');
  const firstHint = searchStatus.textContent;
  let searchRun = 0;
  let searchTimer = 0;

  const cityOf = (location) => (/calgary/i.test(location || '') ? 'Calgary' : /irvine/i.test(location || '') ? 'Irvine' : '');
  const fold = (text) => String(text || '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
  // "Hermès" above the name, and the name without it, as on the cards (the index spells it Hermes).
  const houseOf = (hit) => {
    const house = hit.designer || hit.brand || hit.vendor || '';
    return fold(house) === 'hermes' ? 'Hermès' : house;
  };
  const nameWithoutHouse = (title, house) => {
    const t = String(title || '').trim();
    return house && fold(t).startsWith(`${fold(house)} `) ? t.slice(house.length).trim() : t;
  };
  // Prices in the shopper's currency, as the cards show them (the service keeps the shop's own).
  const priceText = (amount) => {
    const code = window.Shopify?.currency?.active || 'CAD';
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const value = Math.round(Number(amount) * rate);
    if (!Number.isFinite(value) || value <= 0) return '';
    try {
      return `${new Intl.NumberFormat('en-US', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 }).format(value)} ${code}`;
    } catch (_error) {
      return `${value} ${code}`;
    }
  };
  const photoUrl = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_error) {
      return src;
    }
  };

  // The office the chosen pieces are at (null before the first, or for a piece with no office).
  const homeOffice = () => (pieces.length ? OFFICES[pieces[0].city] || null : null);
  const otherCity = () => (pieces[0]?.city === 'Calgary' ? 'Irvine' : 'Calgary');
  const sameKey = (item, handle, variantId) => item.handle === handle && item.variantId === String(variantId || '');
  const hintText = () => (homeOffice() ? `Pieces at ${homeOffice().place}. To see pieces in ${otherCity()} too, book another viewing.` : firstHint);
  const piecesText = () => (pieces.length > 1 ? `${pieces[0].name} and ${pieces.length - 1} more` : pieces[0]?.name || '');

  // Each part stays on one line ("Item 1276608", "$5,900 CAD"); the line breaks between parts.
  const keep = (text) => String(text).replace(/ /g, ' ');
  const metaText = (item) =>
    [item.city ? `In ${item.city}` : 'Location on request', item.sku ? `Item ${item.sku}` : '', priceText(item.amount)].filter(Boolean).map(keep).join(' · ');

  const pieceItem = (item) => {
    const li = document.createElement('li');
    li.className = 'svc-book__piece';
    const photo = document.createElement(item.image ? 'img' : 'span');
    photo.className = 'svc-book__piece-photo';
    if (item.image) {
      photo.src = photoUrl(item.image, 200);
      photo.alt = '';
      photo.width = 64;
      photo.height = 80;
      photo.decoding = 'async';
    }
    const text = document.createElement('div');
    text.className = 'svc-book__piece-text';
    const house = document.createElement('span');
    house.className = 'svc-book__piece-house';
    house.textContent = item.house;
    const title = document.createElement('strong');
    title.className = 'svc-book__piece-title';
    title.textContent = item.name;
    const meta = document.createElement('span');
    meta.className = 'svc-book__piece-meta';
    meta.textContent = metaText(item);
    item.metaEl = meta;
    text.append(house, title, meta);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'svc-book__change';
    remove.textContent = 'Remove';
    remove.setAttribute('aria-label', `Remove ${item.name}`);
    remove.addEventListener('click', () => removePiece(item));
    li.append(photo, text, remove);
    return li;
  };

  // The chosen pieces, then Add Another Piece (or the note that there's no room for more).
  const renderPieces = () => {
    chosenList.replaceChildren(...pieces.map(pieceItem));
    const some = pieces.length > 0;
    const full = pieces.length >= MAX_PIECES;
    pickedBox.hidden = !some;
    howBox.hidden = !some;
    howLegend.textContent = pieces.length > 1 ? 'How Would You Like to See Them?' : 'How Would You Like to See It?';
    fullNote.hidden = !full;
    addButton.hidden = !some || full || !homeOffice() || !findBox.hidden;
    findCancel.hidden = !some;
  };

  const clearSearch = () => {
    searchRun += 1;
    window.clearTimeout(searchTimer);
    searchInput.value = '';
    results.hidden = true;
    results.replaceChildren();
    searchStatus.textContent = hintText();
  };
  const openSearch = () => {
    findBox.hidden = false;
    clearSearch();
    renderPieces();
    searchInput.focus();
  };
  const closeSearch = () => {
    clearSearch();
    findBox.hidden = true;
    renderPieces();
  };

  // In person only at the pieces' office; FaceTime with that office's specialist.
  const setUpHow = () => {
    const office = homeOffice();
    visitChoice.hidden = !office;
    if (office) {
      q('[data-svc-book-visit-label]').textContent = `In Person · ${office.label}`;
      q('[data-svc-book-visit-place]').textContent = office.address;
    }
    form.querySelectorAll('input[name="where"]').forEach((input) => {
      input.checked = !office && input.value === 'facetime';
    });
    pickError.hidden = true;
    if (!office) loadSlots(false);
  };

  const choosePiece = (hit) => {
    if (pieces.length >= MAX_PIECES || pieces.some((item) => sameKey(item, hit.handle, hit.variant_id))) return;
    // One viewing is with one office.
    if (pieces.length && cityOf(hit.location) !== pieces[0].city) return;
    const house = houseOf(hit);
    const item = {
      handle: hit.handle,
      variantId: String(hit.variant_id || ''),
      title: String(hit.title || '').trim(),
      house,
      name: nameWithoutHouse(hit.title, house),
      image: hit.image || '',
      location: hit.location || '',
      city: cityOf(hit.location),
      amount: Number(hit.price_min) || 0,
      // What the alert shows: the listed price in the shop's own currency.
      price: hit.price_min ? Number(hit.price_min).toFixed(2) : '',
      currency: 'CAD',
      sku: '',
    };
    item.url = `${window.location.origin}/products/${item.handle}${item.variantId ? `?variant=${item.variantId}` : ''}`;
    const first = !pieces.length;
    pieces.push(item);
    if (first) {
      chosen = null;
      dayKey = null;
      shownPlan = '';
    }
    closeSearch();
    if (first) setUpHow();
    pickError.hidden = true;
    announce.textContent = `${item.name} added. ${pieces.length === 1 ? '1 piece' : `${pieces.length} pieces`} to see.`;
    (addButton.hidden ? chosenList.lastElementChild?.querySelector('button') : addButton)?.focus({ preventScroll: true });
    // The item number, from the product's own data.
    fetch(`/products/${encodeURIComponent(item.handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product || !pieces.includes(item)) return;
        const variants = product.variants || [];
        const variant = variants.find((other) => String(other.id) === item.variantId) || variants.find((other) => other.available) || variants[0];
        if (!variant) return;
        if (!item.variantId) item.variantId = String(variant.id);
        item.sku = variant.sku || '';
        if (item.metaEl) item.metaEl.textContent = metaText(item);
      })
      .catch(() => {});
  };

  // Back to the start: no pieces, the search open, nothing chosen.
  const resetPieces = () => {
    pieces = [];
    chosen = null;
    dayKey = null;
    shownPlan = '';
    findBox.hidden = false;
    clearSearch();
    renderPieces();
  };

  const removePiece = (item) => {
    pieces = pieces.filter((other) => other !== item);
    announce.textContent = `${item.name} removed.`;
    if (!pieces.length) {
      resetPieces();
      searchInput.focus();
      return;
    }
    renderPieces();
    (addButton.hidden ? toTimes : addButton).focus({ preventScroll: true });
  };

  const resultButton = (hit) => {
    const house = houseOf(hit);
    const city = cityOf(hit.location);
    // Already chosen, or (with no office on record) not at the chosen pieces' office.
    const added = pieces.some((item) => sameKey(item, hit.handle, hit.variant_id));
    const elsewhere = pieces.length > 0 && city !== pieces[0].city;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'svc-book__result';
    button.disabled = added || elsewhere;
    if (hit.image) {
      const img = document.createElement('img');
      img.className = 'svc-book__result-photo';
      img.src = photoUrl(hit.image, 160);
      img.alt = '';
      img.width = 48;
      img.height = 60;
      img.decoding = 'async';
      button.append(img);
    }
    const text = document.createElement('span');
    text.className = 'svc-book__result-text';
    const houseEl = document.createElement('span');
    houseEl.className = 'svc-book__result-house';
    houseEl.textContent = house;
    const nameEl = document.createElement('strong');
    nameEl.textContent = nameWithoutHouse(hit.title, house);
    const metaEl = document.createElement('span');
    metaEl.className = 'svc-book__result-meta';
    metaEl.textContent = [city ? `In ${city}` : '', priceText(hit.price_min), added ? 'Added' : elsewhere ? 'Book separately' : ''].filter(Boolean).join(' · ');
    text.append(houseEl, nameEl, metaEl);
    button.append(text);
    button.addEventListener('click', () => choosePiece(hit));
    return button;
  };

  const searchPieces = async (text) => {
    const run = ++searchRun;
    const url = new URL(searchEndpoint);
    url.searchParams.set('shop', d.shop);
    url.searchParams.set('q', text);
    url.searchParams.set('per_page', '8');
    url.searchParams.set('availability', 'in_stock');
    url.searchParams.set('analytics', 'false');
    // Once a piece is chosen, only pieces at its office.
    const office = homeOffice();
    if (office && pieces[0].location) url.searchParams.set('location', pieces[0].location);
    const where = office ? ` at ${office.place}` : '';
    searchStatus.textContent = 'Searching…';
    try {
      const response = await fetch(url, { credentials: 'omit', headers: { Accept: 'application/json' } });
      const data = response.ok ? await response.json() : null;
      if (run !== searchRun) return;
      if (!data) throw new Error(String(response.status));
      const hits = (data.hits || []).filter((hit) => hit.handle && hit.availability !== 'out_of_stock');
      results.replaceChildren(
        ...hits.map((hit) => {
          const li = document.createElement('li');
          li.append(resultButton(hit));
          return li;
        }),
      );
      results.hidden = !hits.length;
      const found = Number(data.found) || hits.length;
      searchStatus.textContent = !hits.length
        ? office
          ? `Nothing for sale${where} matches “${text}”. To see pieces in ${otherCity()}, book another viewing.`
          : `Nothing for sale matches “${text}”. Try another name or the item number.`
        : found > hits.length
          ? `The first ${hits.length} of ${found} pieces${where}. Add a word or the item number to narrow it down.`
          : `${hits.length === 1 ? '1 piece' : `${hits.length} pieces`} for sale${where}. Choose the one you’d like to see.`;
    } catch (_error) {
      if (run !== searchRun) return;
      results.hidden = true;
      searchStatus.textContent = `We couldn’t search just now. Please try again, or call ${d.mainPhone}.`;
    }
  };

  searchInput.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    pickError.hidden = true;
    const text = searchInput.value.trim();
    if (text.length < 2) {
      searchRun += 1;
      results.hidden = true;
      results.replaceChildren();
      searchStatus.textContent = hintText();
      return;
    }
    searchTimer = window.setTimeout(() => searchPieces(text), 250);
  });
  // Enter searches at once (and never submits the form); Escape closes an added search.
  searchInput.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && pieces.length) {
      event.preventDefault();
      closeSearch();
      addButton.focus({ preventScroll: true });
      return;
    }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    window.clearTimeout(searchTimer);
    const text = searchInput.value.trim();
    if (text.length >= 2) searchPieces(text);
  });

  addButton.addEventListener('click', openSearch);
  findCancel.addEventListener('click', () => {
    closeSearch();
    (addButton.hidden ? toTimes : addButton).focus({ preventScroll: true });
  });

  // In person or FaceTime: the times start loading at once.
  form.querySelectorAll('input[name="where"]').forEach((input) => {
    input.addEventListener('change', () => {
      pickError.hidden = true;
      loadSlots(false);
    });
  });

  toTimes.addEventListener('click', () => {
    if (!pieces.length) {
      pickError.textContent = 'Search for a piece you’d like to see, then choose it.';
      pickError.hidden = false;
      searchInput.focus();
      return;
    }
    const p = plan();
    if (!p) {
      pickError.textContent = 'Choose in person or FaceTime.';
      pickError.hidden = false;
      return;
    }
    // A different specialist or kind: their own times, so nothing chosen carries over.
    if (keyOfPlan(p) !== shownPlan) {
      chosen = null;
      dayKey = null;
      shownPlan = keyOfPlan(p);
    }
    phoneLabel.textContent = isVisit() ? 'Phone' : 'FaceTime Number';
    show('2');
  });

  toDetails.addEventListener('click', () => {
    fillSummary();
    show('3');
  });
  q('[data-svc-book-change]').addEventListener('click', () => show('2'));

  // ------------------------------------------------------------ open times

  const entryFor = (key) => {
    if (!cache.has(key)) cache.set(key, { data: null, at: 0, loading: null });
    return cache.get(key);
  };

  const loadSlots = (fresh = false) => {
    const p = plan();
    if (!p) return Promise.resolve(null);
    const entry = entryFor(keyOfPlan(p));
    if (!fresh && entry.data && Date.now() - entry.at < 60000) return Promise.resolve(entry.data);
    if (entry.loading && !fresh) return entry.loading;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 9000);
    const url = `${d.endpoint}?shop=${encodeURIComponent(d.shop)}&specialist=${encodeURIComponent(p.specialist)}&kind=${p.kind}`;
    entry.loading = fetch(url, { signal: controller.signal, credentials: 'omit' })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
      .then((data) => {
        entry.data = data && Array.isArray(data.slots) ? data : null;
        entry.at = Date.now();
        return entry.data;
      })
      .catch(() => {
        entry.data = null;
        return null;
      })
      .finally(() => {
        window.clearTimeout(timer);
        entry.loading = null;
      });
    return entry.loading;
  };

  const button = (className, label) => {
    const element = document.createElement('button');
    element.type = 'button';
    element.className = className;
    if (label) element.textContent = label;
    return element;
  };

  const PERIODS = [
    { name: 'Morning', test: (hour) => hour < 12 },
    { name: 'Afternoon', test: (hour) => hour >= 12 && hour < 17 },
    { name: 'Evening', test: (hour) => hour >= 17 },
  ];

  const renderTimes = (byDay) => {
    const slots = byDay.get(dayKey) || [];
    const groups = PERIODS.map((period) => ({ ...period, slots: slots.filter((slot) => period.test(slot.start.getHours())) })).filter((group) => group.slots.length);
    timesEl.replaceChildren(
      ...groups.map((group) => {
        const wrap = document.createElement('div');
        wrap.className = 'svc-book__period';
        const heading = document.createElement('p');
        heading.className = 'svc-book__period-name';
        heading.textContent = group.name;
        const grid = document.createElement('div');
        grid.className = 'svc-book__time-grid';
        group.slots.forEach((slot) => {
          const time = button('svc-book__time', timeText(slot.start));
          time.setAttribute('aria-pressed', String(Boolean(chosen && chosen.iso === slot.iso)));
          time.setAttribute('aria-label', whenText(slot.start));
          time.addEventListener('click', () => {
            chosen = slot;
            timesEl.querySelectorAll('.svc-book__time').forEach((other) => other.setAttribute('aria-pressed', String(other === time)));
            toDetails.disabled = false;
            takenEl.hidden = true;
          });
          grid.append(time);
        });
        wrap.append(heading, grid);
        return wrap;
      }),
    );
  };

  const describe = (p, minutes) => {
    const name = nameOf(p.specialist);
    return p.kind === 'visit' ? `A ${minutes ? `${minutes}-minute ` : ''}visit with ${name} at ${p.place}.` : `A ${minutes ? `${minutes}-minute ` : 'short '}FaceTime with ${name}.`;
  };

  let renderRun = 0;
  const renderSlots = async (fresh = false) => {
    const run = ++renderRun;
    const p = plan();
    const name = nameOf(p.specialist);
    const entry = entryFor(keyOfPlan(p));
    if (!entry.data || fresh) {
      loadingEl.hidden = false;
      calendar.hidden = true;
      unavailable.hidden = true;
    }
    const data = await loadSlots(fresh);
    if (run !== renderRun) return;
    loadingEl.hidden = true;

    const slots = data ? data.slots.map((slot) => ({ iso: slot.start, start: new Date(slot.start), end: new Date(slot.end) })) : [];
    if (!slots.length) {
      // No times to show: they can still leave their details and the specialist arranges one.
      calendar.hidden = true;
      unavailable.hidden = false;
      unavailable.textContent = data
        ? `There are no open times in the next two weeks. Leave your details and ${name} will find a time with you.`
        : `We couldn’t load ${name}’s calendar just now. Leave your details and ${name} will arrange a time with you.`;
      zoneEl.textContent = describe(p);
      chosen = null;
      toDetails.disabled = false;
      return;
    }

    const minutes = data.lengthMinutes || (p.kind === 'visit' ? 30 : 15);
    zoneEl.textContent = `${describe(p, minutes)} Times are in ${zoneName}.${data.mode === 'request' ? ` ${name} will confirm your time.` : ''}`;
    unavailable.hidden = true;
    calendar.hidden = false;

    const byDay = new Map();
    slots.forEach((slot) => {
      const key = keyOf(slot.start);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(slot);
    });
    if (chosen && !slots.some((slot) => slot.iso === chosen.iso)) chosen = null;
    if (!dayKey || !byDay.has(dayKey)) dayKey = chosen ? keyOf(chosen.start) : keyOf(slots[0].start);
    toDetails.disabled = !chosen;

    // A calendar, Sunday to Saturday: from this week's Sunday to the Saturday after the last open
    // day. Days already gone show greyed, like the closed weekends.
    const days = [];
    const lastKey = keyOf(slots[slots.length - 1].start);
    const todayKey = keyOf(new Date());
    const cursor = new Date();
    cursor.setHours(12, 0, 0, 0);
    cursor.setDate(cursor.getDate() - cursor.getDay());
    let reachedLast = false;
    for (let i = 0; i < 42; i++) {
      days.push(new Date(cursor));
      if (keyOf(cursor) === lastKey) reachedLast = true;
      if (reachedLast && cursor.getDay() === 6) break;
      cursor.setDate(cursor.getDate() + 1);
    }
    const first = days[0];
    const end = days[days.length - 1];
    const month = format({ month: 'long' });
    monthEl.textContent = month.format(first) === month.format(end) ? format({ month: 'long', year: 'numeric' }).format(first) : `${month.format(first)} – ${format({ month: 'long', year: 'numeric' }).format(end)}`;

    let pastToday = false;
    daysEl.replaceChildren(
      ...days.map((day) => {
        const key = keyOf(day);
        const open = byDay.has(key);
        if (key === todayKey) pastToday = true;
        const chip = button('svc-book__day');
        const date = document.createElement('strong');
        date.textContent = String(day.getDate());
        chip.append(date);
        // The 1st of a month says which month it is.
        if (day.getDate() === 1) {
          const label = document.createElement('span');
          label.textContent = format({ month: 'short' }).format(day);
          chip.prepend(label);
        }
        if (key === todayKey) chip.classList.add('is-today');
        if (!pastToday) chip.classList.add('is-past');
        chip.disabled = !open;
        chip.setAttribute('aria-pressed', String(key === dayKey));
        chip.setAttribute('aria-label', `${dayText(day)}${key === todayKey ? ', today' : ''}${open ? '' : ', no times'}`);
        chip.addEventListener('click', () => {
          dayKey = key;
          daysEl.querySelectorAll('.svc-book__day').forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
          renderTimes(byDay);
        });
        return chip;
      }),
    );
    renderTimes(byDay);
  };

  // ------------------------------------------------------------ details and booking

  const fillSummary = () => {
    const p = plan();
    const name = nameOf(p.specialist);
    const data = entryFor(keyOfPlan(p)).data;
    const minutes = (data && data.lengthMinutes) || (p.kind === 'visit' ? 30 : 15);
    const how = p.kind === 'visit' ? `with ${name} at ${p.place} · ${minutes} minutes` : `FaceTime with ${name} · ${minutes} minutes`;
    if (chosen) {
      whenEl.textContent = whenText(chosen.start);
      withEl.textContent = `${piecesText()} · ${how}`;
    } else {
      whenEl.textContent = 'A time that suits you';
      withEl.textContent = `${piecesText()} · ${name} will be in touch to arrange it`;
    }
  };

  // U.S. and Canadian numbers (and the rest of +1) are formatted as (949) 555-0123; anywhere else
  // the shopper starts with + and their country code.
  const NANP_COUNTRIES = ['US', 'CA', 'PR', 'VI', 'GU', 'AS', 'MP', 'BS', 'BB', 'BM', 'JM', 'TT', 'KY', 'VG', 'AG', 'DM', 'GD', 'KN', 'LC', 'VC', 'SX', 'TC', 'AI', 'MS', 'DO'];
  const localIsNanp = !d.country || NANP_COUNTRIES.includes(d.country);
  const phoneInput = form.elements.phone;
  const emailInput = form.elements.email;
  const nameInput = form.elements.name;
  phoneInput.placeholder = localIsNanp ? '(949) 555-0123' : '+44 7700 900123';
  const fieldError = (input) => q(`[data-svc-book-field-error="${input.name}"]`);

  const formatNanp = (digits) => {
    if (digits.length <= 3) return digits.length ? `(${digits}` : '';
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  /** { ok, value (what's sent), message } for what's in the phone field. */
  const checkPhone = (raw) => {
    const value = raw.trim();
    const digits = value.replace(/\D/g, '');
    if (!digits) return { ok: false, message: isVisit() ? `Add a phone number so ${nameOf(plan().specialist)} can reach you.` : 'Add the number you use for FaceTime.' };
    if (/[^+\d\s().-]/.test(value)) return { ok: false, message: 'Use numbers only.' };
    const international = value.startsWith('+');
    if (international && !digits.startsWith('1')) {
      if (digits.length < 8 || digits.length > 15) return { ok: false, message: 'Check the number and the country code.' };
      return { ok: true, value: `+${digits}` };
    }
    if (!international && !localIsNanp) return { ok: false, message: 'Start with + and your country code, like +44.' };
    const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (local.length !== 10) return { ok: false, message: 'Enter all 10 digits, like (949) 555-0123.' };
    if (!/^[2-9]\d{2}[2-9]/.test(local)) return { ok: false, message: 'Check the area code.' };
    return { ok: true, value: `+1 ${formatNanp(local)}` };
  };
  const checkEmail = (raw) => {
    const value = raw.trim();
    if (!value) return { ok: false, message: 'Add your email for the calendar invite.' };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) return { ok: false, message: 'Check your email address.' };
    return { ok: true, value };
  };
  const checkName = (raw) => (raw.trim().length >= 2 ? { ok: true, value: raw.trim() } : { ok: false, message: 'Add your first name.' });
  const CHECKS = { name: checkName, phone: checkPhone, email: checkEmail };

  const showFieldError = (input, message) => {
    const slot = fieldError(input);
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      if (slot) {
        slot.textContent = message;
        slot.hidden = false;
      }
    } else {
      input.removeAttribute('aria-invalid');
      if (slot) slot.hidden = true;
    }
  };

  // Format a +1 number as it's typed (only while typing at the end, so editing in the middle works).
  phoneInput.addEventListener('input', (event) => {
    const value = phoneInput.value;
    if (value.trim().startsWith('+') || phoneInput.selectionStart !== value.length || event.inputType === 'deleteContentBackward') return;
    if (!localIsNanp) return;
    let digits = value.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length > 10) return;
    phoneInput.value = formatNanp(digits);
  });

  // Checked when they leave a field (not while typing), and cleared as soon as it's right.
  [nameInput, phoneInput, emailInput].forEach((input) => {
    input.addEventListener('blur', () => {
      if (!input.value.trim()) return;
      const result = CHECKS[input.name](input.value);
      showFieldError(input, result.ok ? '' : result.message);
    });
    input.addEventListener('input', () => {
      if (input.getAttribute('aria-invalid') && CHECKS[input.name](input.value).ok) showFieldError(input, '');
    });
  });

  const fail = (text, field) => {
    if (field) {
      showFieldError(field, text);
      field.focus();
      return;
    }
    errorEl.textContent = text;
    errorEl.hidden = false;
  };

  form.addEventListener('input', () => {
    if (!errorEl.hidden) errorEl.hidden = true;
  });

  const busy = (on) => {
    submit.disabled = on;
    if (on) submit.setAttribute('aria-busy', 'true');
    else submit.removeAttribute('aria-busy');
    submit.textContent = on ? 'Booking…' : 'Book My Viewing';
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    // Enter in an earlier step moves on rather than booking.
    if (current === '1') return toTimes.click();
    if (current === '2') {
      if (!toDetails.disabled) toDetails.click();
      return;
    }
    if (current !== '3') return;

    const checked = {};
    for (const input of [nameInput, phoneInput, emailInput]) {
      const result = CHECKS[input.name](input.value);
      if (!result.ok) return fail(result.message, input);
      checked[input.name] = result.value;
    }
    const { name, phone, email } = checked;
    const p = plan();
    const specialistName = nameOf(p.specialist);

    busy(true);
    // The same booking as a product page's private viewing (assets/pdp-viewing.js): the first
    // piece as the viewing's own, the others in pieces (the app checks each one the same way).
    const [first, ...others] = pieces;
    const payload = {
      reason: p.kind === 'visit' ? 'visit' : 'photos',
      kind: p.kind,
      message: '',
      name,
      phone,
      email,
      website: form.elements.website.value || '',
      specialist: p.specialist,
      start: chosen ? chosen.iso : '',
      timeZone: zone,
      productHandle: first.handle,
      productTitle: first.title,
      sku: first.sku,
      variantId: first.variantId,
      productUrl: first.url,
      price: first.price,
      currency: first.currency,
      location: first.city,
      pieces: others.map((item) => ({ productHandle: item.handle, variantId: item.variantId, sku: item.sku })),
      country: d.country,
      pageUrl: window.location.href,
    };

    try {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 15000);
      const response = await fetch(`${d.endpoint}?shop=${encodeURIComponent(d.shop)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
        signal: controller.signal,
      });
      window.clearTimeout(timer);
      const body = await response.json().catch(() => ({}));
      busy(false);
      if (response.ok && body.status === 'ok') return finish(body, p, { phone, email });
      if (response.status === 409) {
        // Someone took that time a moment ago: back to the times, freshly loaded.
        chosen = null;
        entryFor(keyOfPlan(p)).data = null;
        show('2');
        takenEl.textContent = body.message || 'That time was just taken. Please choose another.';
        takenEl.hidden = false;
        return;
      }
      return fail(body.message || `Something went wrong. Please try again, or call or text ${specialistName} at ${phoneOf(p.specialist)}.`);
    } catch (_error) {
      busy(false);
      return fail(`We couldn’t book just now. Please try again, or call or text ${specialistName} at ${phoneOf(p.specialist)}.`);
    }
  });

  // ------------------------------------------------------------ done

  let record = null;

  const showDone = (saved, options) => {
    record = saved;
    const visit = saved.kind === 'visit';
    const name = nameOf(saved.specialist);
    const start = saved.start ? new Date(saved.start) : null;
    q('[data-svc-book-done-title]').textContent = saved.booked ? (visit ? 'Your Visit Is Booked' : 'Your Viewing Is Booked') : 'Request Received';
    const when = q('[data-svc-book-done-when]');
    when.textContent = start ? `${whenText(start)} (${zoneName})` : '';
    when.hidden = !start;
    const where = saved.address ? `${saved.place}, ${saved.address}` : saved.place;
    q('[data-svc-book-done-text]').textContent = saved.booked
      ? visit
        ? `${name} will meet you at ${where}. Your calendar invite is on its way to ${saved.email}.`
        : `${name} will FaceTime you at ${unbroken(saved.phone)}. Your calendar invite is on its way to ${saved.email}.`
      : start
        ? `${name} will confirm this time with you shortly, by text or email.`
        : `${name} will be in touch shortly to arrange a time for your ${visit ? 'visit' : 'FaceTime viewing'}.`;
    calendars.hidden = !(saved.booked && start);
    if (!calendars.hidden) fillCalendarLinks();
    again.hidden = false;
    show('done', options);
  };

  const finish = (body, p, person) => {
    const saved = {
      kind: p.kind,
      specialist: p.specialist,
      city: p.city,
      place: p.place,
      address: p.kind === 'visit' && body.place && body.place !== 'FaceTime' ? body.place : null,
      booked: Boolean(body.booked),
      start: body.start || null,
      end: body.end || null,
      icalUid: body.icalUid || null,
      productTitle: pieces.length > 1 ? `${pieces[0].title} + ${pieces.length - 1} more` : pieces[0].title,
      productUrls: pieces.map((item) => item.url),
      phone: person.phone,
      email: person.email,
    };
    try {
      sessionStorage.setItem(STORE, JSON.stringify(saved));
    } catch (_error) {
      // private mode: the confirmation still shows now
    }
    // The time just booked is no longer open: fresh times for the next booking.
    cache.clear();
    showDone(saved);
  };

  // Another viewing: back to the first step, with their name, number and email kept.
  again.addEventListener('click', () => {
    try {
      sessionStorage.removeItem(STORE);
    } catch (_error) {
      // nothing saved
    }
    record = null;
    resetPieces();
    again.hidden = true;
    show('1');
  });

  // Add to Your Calendar: Google and Outlook open their own calendars in a new tab, filled in;
  // Apple gets a calendar file, with the same UID as Google's invite so it doesn't double up.
  // As the product pages name it, so the shopper's own copy matches Google's invite.
  const eventTitle = () => `Luxe Du Jour ${record.kind === 'visit' ? 'In-Person' : 'FaceTime'} Viewing${record.productTitle ? ` · ${record.productTitle}` : ''}`;
  const eventDetails = () =>
    [
      record.kind === 'visit' ? `${nameOf(record.specialist)} will meet you at ${record.place}.` : `${nameOf(record.specialist)} will FaceTime you at ${record.phone}.`,
      ...(record.productUrls || (record.productUrl ? [record.productUrl] : [])),
    ].join('\n');
  const eventPlace = () => (record.kind === 'visit' ? (record.address ? `Luxe Du Jour, ${record.address}` : `Luxe Du Jour, ${record.city}`) : 'FaceTime');
  const stamp = (value) => new Date(value).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

  const fillCalendarLinks = () => {
    if (!record || !record.start || !record.end) return;
    const google = new URLSearchParams({
      action: 'TEMPLATE',
      text: eventTitle(),
      dates: `${stamp(record.start)}/${stamp(record.end)}`,
      details: eventDetails(),
      location: eventPlace(),
    });
    calendars.querySelector('[data-svc-book-cal="google"]').href = `https://calendar.google.com/calendar/render?${google}`;
    const outlook = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: eventTitle(),
      startdt: new Date(record.start).toISOString(),
      enddt: new Date(record.end).toISOString(),
      body: eventDetails(),
      location: eventPlace(),
    });
    calendars.querySelector('[data-svc-book-cal="outlook"]').href = `https://outlook.live.com/calendar/0/deeplink/compose?${outlook}`;
  };

  calendars.querySelector('[data-svc-book-cal="apple"]').addEventListener('click', () => {
    if (!record || !record.start || !record.end) return;
    const text = (value) => String(value).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1');
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Luxe Du Jour//Appointments//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${record.icalUid || `${stamp(record.start)}-appointment@ldj.com`}`,
      `DTSTAMP:${stamp(Date.now())}`,
      `DTSTART:${stamp(record.start)}`,
      `DTEND:${stamp(record.end)}`,
      `SUMMARY:${text(eventTitle())}`,
      `DESCRIPTION:${text(eventDetails())}`,
      `LOCATION:${text(eventPlace())}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ];
    const url = URL.createObjectURL(new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' }));
    const link = Object.assign(document.createElement('a'), { href: url, download: 'ldj-appointment.ics' });
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  });

  // ------------------------------------------------------------ opening

  // Booked earlier in this visit (and not over yet): the card opens on the confirmation.
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORE) || 'null');
    if (saved && saved.kind && (!saved.end || new Date(saved.end) > new Date())) showDone(saved, { focus: false, scroll: false });
  } catch (_error) {
    // nothing saved
  }
})();
